import useAccessControl from '../../../../legacy/components/AccessControl/useAccessControl'
import { FeatureToggle } from '../../../../legacy/contexts/featureToggles/FeatureToggleContext'

/**
 * Feature toggle name for the tool satisfaction survey.
 * This roulette controls whether users see the survey after completing workflows.
 */
export const TOOL_SURVEY_FEATURE_TOGGLE = FeatureToggle.ToolSatisfactionSurvey

/**
 * Hook to check if the tool satisfaction survey feature is enabled.
 *
 * This hook checks both user-level and warehouse-level feature toggles
 * to determine if the survey should be shown.
 *
 * Note: The `ToolSurvey` component calls this hook internally, so you typically
 * don't need to use this hook directly. Just render `<ToolSurvey />` and it will
 * handle the roulette check automatically.
 *
 * @returns {boolean} Whether the tool satisfaction survey is enabled for the current user/warehouse
 *
 * @example
 * ```tsx
 * // The ToolSurvey component handles the roulette check internally,
 * // so you can just render it directly:
 * <ToolSurvey
 *   workflowSlug="my-workflow"
 *   isTriggered={workflowCompleted}
 *   onClose={handleClose}
 * />
 *
 * // If you need to check access separately (e.g., for conditional logic):
 * import { useToolSurveyAccess } from '@retailer-platform/dashboard/gin-and-tonic'
 *
 * const isToolSurveyEnabled = useToolSurveyAccess()
 * ```
 */
export const useToolSurveyAccess = (): boolean => {
  const hasAccess = useAccessControl()

  // Check user-level feature toggle
  const hasUserAccess = hasAccess({
    featureToggles: TOOL_SURVEY_FEATURE_TOGGLE,
  })

  // Check warehouse-level feature toggle
  const hasWarehouseAccess = hasAccess({
    warehouseFeatureToggles: [TOOL_SURVEY_FEATURE_TOGGLE],
  })

  return hasUserAccess || hasWarehouseAccess
}
